import { ApiRequestError } from '../api';
import { clientLogger } from '../utils/client-logger';
import type { PanelEligibility } from '../hooks/useAmazonDataConnection';

export type TerminalError = { message: string; code?: string };

export function getSigninError(err: unknown): TerminalError {
  const message = err instanceof Error ? err.message : String(err);
  const code = err instanceof ApiRequestError ? err.code : undefined;
  return { message, code };
}

/**
 * Shared `handleSigninDone` for the retailer hooks whose post-signin step is
 * "call onAuthComplete, then load purchase history" — fires on SUCCESS from
 * either the direct signin() response or the 2FA iframe's postMessage bridge.
 */
export function createSigninDoneHandler(
  loadPurchaseHistory: () => Promise<void>,
  onAuthComplete: ((panelEligibility?: PanelEligibility) => void) | undefined,
  onConnectionError: ((message: string, code?: string) => void) | undefined,
  logLabel: string
): (
  status: 'SUCCESS' | 'ERROR',
  error?: string,
  panelEligibility?: PanelEligibility
) => void {
  return (status, error, panelEligibility) => {
    if (status === 'ERROR') {
      onConnectionError?.(error ?? 'Sign-in failed');
      return;
    }
    onAuthComplete?.(panelEligibility);
    loadPurchaseHistory().catch((err) => {
      const { message, code } = getSigninError(err);
      clientLogger.info(`${logLabel} purchase history error:`, { error: err });
      onConnectionError?.(message, code);
    });
  };
}
